import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { IconMagnifierOutline18 as SearchIcon } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { Shortcut } from "@productlane/ui"

import type {
	CustomBodyParams,
	SlashCommandDefinition,
	SlashCommandListProps,
} from "./types"
import { SlashCommandsDialog } from "./slash-commands-dialog"

export default function SlashCommandsList(props: SlashCommandListProps) {
	const [selected, setSelected] = useState("")
	const listRef = useRef<HTMLDivElement | null>(null)
	const { editor, range, items, query, setOnKeyDownHandler } = props
	const [showCustomBody, setShowCustomBody] = useState(false)
	const [showCustomBodyLoading, setShowCustomBodyLoading] = useState(false)

	const groupedItems = useMemo(() => {
		const groups: Record<string, SlashCommandDefinition[]> = {}

		for (const item of items) {
			const group = item.group || "other"
			if (!groups[group]) groups[group] = []
			groups[group].push(item)
		}

		return groups
	}, [items])

	const getSelectedIndex = useCallback(
		() => items.findIndex(({ key }) => selected === key),
		[items, selected],
	)
	const getPrevIndex = useCallback(() => {
		const current = getSelectedIndex()
		return current === 0 ? items.length - 1 : current - 1
	}, [getSelectedIndex, items.length])

	const getNextIndex = useCallback(() => {
		const current = getSelectedIndex()
		return current === items.length - 1 ? 0 : current + 1
	}, [getSelectedIndex, items.length])

	const runCommand = useCallback(
		(
			item: SlashCommandDefinition<CustomBodyParams>,
			customBodyParams: CustomBodyParams,
		) => {
			setShowCustomBody(false)

			props.command({
				editor,
				range,
				command: (props) => item.command({ ...props, customBodyParams }),
			})
		},
		[editor, props, range],
	)

	const triggerCommandItem = useCallback(
		(item: SlashCommandDefinition<CustomBodyParams>) => {
			if (item.customBody) {
				setSelected(item.key)
				setShowCustomBodyLoading(false)
				setShowCustomBody(true)
				return
			}

			runCommand(item, {})
		},
		[runCommand],
	)

	const onCustomBodySubmit = useCallback(
		(customBodyParams: CustomBodyParams) => {
			const item = items[getSelectedIndex()]
			if (!item) return
			runCommand(item, customBodyParams)
		},
		[getSelectedIndex, items, runCommand],
	)

	const onCommandSelect = useCallback(() => {
		const item = items[getSelectedIndex()]
		if (!item) return
		triggerCommandItem(item)
	}, [getSelectedIndex, items, triggerCommandItem])

	useEffect(() => {
		setOnKeyDownHandler(({ event }) => {
			switch (event.key) {
				case "ArrowUp":
					setSelected(items[getPrevIndex()]?.key ?? "")
					return true
				case "ArrowDown":
					setSelected(items[getNextIndex()]?.key ?? "")
					return true
				case "Enter":
					onCommandSelect()
					return true
				case "Tab":
					onCommandSelect()
					return true
				default:
					return false
			}
		})
	}, [
		getNextIndex,
		getPrevIndex,
		items,
		onCommandSelect,
		runCommand,
		setOnKeyDownHandler,
		showCustomBody,
	])

	useEffect(() => {
		const [firstItem] = items
		if (firstItem) {
			setSelected(firstItem.key)
		} else {
			setSelected("")
		}
		setShowCustomBody(false)
	}, [items])

	// Only a short viewport still scrolls; keyboard nav keeps the row in view.
	useEffect(() => {
		listRef.current
			?.querySelector(`[data-slash-key="${selected}"]`)
			?.scrollIntoView({ block: "nearest" })
	}, [selected])

	const hideCustomBody = useCallback(() => {
		setShowCustomBody(false)
		editor.view.focus()
	}, [editor.view])

	const selectedItem = items[getSelectedIndex()]
	const CustomBody = selectedItem?.customBody
	const title = selectedItem?.bodyTitle || selectedItem?.title || null

	return (
		<div
			className={cn(
				"relative overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10",
				showCustomBody ? "w-[22rem] max-w-[calc(100vw-16px)]" : "w-60",
				items.length === 0 && "opacity-0",
			)}
		>
			{showCustomBody ? (
				// Forms sit outside the list's scroller; inside it, autofocus scrolled the title and padding out of view.
				<div className="max-h-[calc(100dvh-16px)] overflow-y-auto">
					<SlashCommandsDialog
						showLoading={showCustomBodyLoading}
						CustomBody={CustomBody}
						title={title}
						onSubmit={onCustomBodySubmit}
						onCancel={hideCustomBody}
						setShowLoading={setShowCustomBodyLoading}
					/>
				</div>
			) : (
				<>
					{/* Typing after "/" stays in the editor, so this row shows the query rather than owning focus. */}
					<div className="flex h-9 items-center gap-2 border-b border-gray-200 px-3 dark:border-gray-700/80">
						<SearchIcon className="h-3.5 w-3.5 flex-none text-tertiary" />
						<span
							className={cn(
								"min-w-0 truncate text-sm",
								query ? "text-primary" : "text-tertiary",
							)}
						>
							{query || "Search blocks"}
						</span>
					</div>
					<div
						ref={listRef}
						className="max-h-[min(360px,calc(100dvh-56px))] overflow-y-auto p-1 pb-2"
					>
						<ul className="m-0 flex list-none flex-col p-0">
							{Object.entries(groupedItems).map(
								([group, groupItems], index) => (
									<li key={group}>
										<ul className="m-0 flex flex-col gap-0 p-0">
											{groupItems.map((item) => (
												<button
													type="button"
													onClick={() => triggerCommandItem(item)}
													onMouseMove={() => {
														setSelected(item.key)
													}}
													onMouseDown={(event) => {
														event.preventDefault()
														setSelected(item.key)
													}}
													className={cn(
														"flex h-7 w-full items-center justify-between gap-3 rounded-md px-2 text-base",
														selected === item.key &&
															"bg-gray-100 dark:bg-gray-700/50",
													)}
													key={item.key}
													data-slash-key={item.key}
												>
													<div className="flex min-w-0 flex-row items-center gap-2.5">
														{item.icon}
														<div className="truncate text-sm">{item.title}</div>
													</div>

													{item.shortcutIcons && (
														<div className="flex flex-row items-center gap-[3px]">
															{item.shortcutIcons.map((icon, index) => {
																return (
																	<Shortcut
																		variant="ghost"
																		key={index + icon}
																		className={cn("ml-auto", {
																			" capitalize": icon === "ctrl",
																		})}
																	>
																		{icon}
																	</Shortcut>
																)
															})}
														</div>
													)}
												</button>
											))}
										</ul>
										{index < Object.keys(groupedItems).length - 1 && (
											<hr className="-mx-1 my-1 border-gray-200 dark:border-gray-700/80" />
										)}
									</li>
								),
							)}
						</ul>
					</div>
				</>
			)}
		</div>
	)
}
